/**
 * Category Alias Operations
 *
 * Handles category alias resolution for renamed/deleted categories.
 * When HybridCategorizer returns an old category name (e.g., "Groceries"),
 * we resolve it through the alias chain to the current name (e.g., "Food Shopping").
 */

import { query, execute, saveDB } from '../../db';

/**
 * Create or update a category alias
 */
export async function setCategoryAlias(oldName: string, newName: string): Promise<{ success: boolean; error?: string }> {
  try {
    execute('INSERT OR REPLACE INTO category_aliases (old_name, new_name) VALUES (?, ?)', [oldName, newName]);
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[categoryAliasOperations] Error setting alias:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get all category aliases
 */
export async function getAllAliases(): Promise<Record<string, string>> {
  try {
    const rows = query<{ old_name: string; new_name: string }>('SELECT old_name, new_name FROM category_aliases');
    const aliases: Record<string, string> = {};
    for (const row of rows) {
      aliases[row.old_name] = row.new_name;
    }
    return aliases;
  } catch (error) {
    console.error('[categoryAliasOperations] Error getting aliases:', error);
    return {};
  }
}

/**
 * Resolve a category name through the alias chain
 * Handles chains like: "Groceries" → "Food & Groceries" → "Food Shopping"
 * Falls back to "Uncategorized" if the final category doesn't exist
 */
export async function resolveCategoryAlias(categoryName: string): Promise<string> {
  try {
    // First, check if the category exists as-is
    const categoryExists = query<{ name: string }>('SELECT name FROM categories WHERE name = ?', [categoryName]);
    if (categoryExists.length > 0) {
      return categoryName;
    }

    // Follow the alias chain (max 10 hops to prevent infinite loops)
    let currentName = categoryName;
    const visited = new Set<string>();
    let maxHops = 10;

    while (maxHops > 0) {
      // Check if we've seen this name before (circular reference protection)
      if (visited.has(currentName)) {
        console.warn(`[categoryAliasOperations] Circular alias detected for "${categoryName}"`);
        return 'Uncategorized';
      }
      visited.add(currentName);

      // Look up alias
      const aliasRow = query<{ new_name: string }>('SELECT new_name FROM category_aliases WHERE old_name = ?', [
        currentName
      ]);

      if (aliasRow.length === 0) {
        // No alias found - check if current name is a valid category
        const exists = query<{ name: string }>('SELECT name FROM categories WHERE name = ?', [currentName]);
        return exists.length > 0 ? currentName : 'Uncategorized';
      }

      // Follow the alias
      currentName = aliasRow[0].new_name;

      // Check if the aliased category exists
      const exists = query<{ name: string }>('SELECT name FROM categories WHERE name = ?', [currentName]);
      if (exists.length > 0) {
        return currentName;
      }

      maxHops--;
    }

    // Max hops reached - fall back to Uncategorized
    console.warn(`[categoryAliasOperations] Max alias hops reached for "${categoryName}"`);
    return 'Uncategorized';
  } catch (error) {
    console.error('[categoryAliasOperations] Error resolving alias:', error);
    return 'Uncategorized';
  }
}

/**
 * Delete a category alias
 */
export async function deleteCategoryAlias(oldName: string): Promise<{ success: boolean; error?: string }> {
  try {
    execute('DELETE FROM category_aliases WHERE old_name = ?', [oldName]);
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[categoryAliasOperations] Error deleting alias:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
